/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAsyncThunk } from '@reduxjs/toolkit';

import { ranProcessEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { Assistant, Multilingual } from '../../../../ts/assistant';
import {
  AppDispatch,
  AsyncAppThunk,
  CreateAsyncThunk
} from '../../../../ts/state';
import {
  getCounterExamples,
  listActions
} from '../../../actions/store/actions/actions';
import { listDataTypes } from '../../../actions/store/data-types/actions';
import { fetchEntities } from '../../../actions/store/entities/actions';
import { fetchIntents } from '../../../actions/store/intents/actions';
import { listSystemDataTypes } from '../../../actions/store/system-data-types/actions';
import { listVariables } from '../../../actions/store/variables/actions';
import { fetchNodes } from '../../../dialog/store/dialog/actions';
import * as API from '../../api/assistants';
import * as releasesAPI from '../../api/releases';
import { getAssistant } from '../assistants/actions';
import { listEnvironments } from '../environments/actions';
import { addErrorMessage, addMessage, ToastStatus } from '../messages/actions';
import { sleep } from '../skills/helper';
import {
  DownloadUploadType,
  MultilingualStatusType,
  TabType,
  isProcessing,
  triggerSaveZip
} from './helper';

export const enableMultilingual = createAsyncThunk<
  Assistant,
  Partial<Assistant>,
  CreateAsyncThunk
>('multilingual/enable', async (params, { dispatch }) => {
  const { assistant_id: id, ...rest } = params;

  try {
    const response = await API.updateAssistant(id, rest, {});
    const multilingual = response.multi_lingual;

    // Kick off a process to check on the multilingual enablement status
    await dispatch(checkMultilingualEnablementProgress(id, multilingual));

    return response;
  } catch (error) {
    dispatch(
      addMessage({
        message: 'Multilingual failed to activate. Please try again',
        status: ToastStatus.ERROR
      })
    );
    ranProcessEvents.multilingualActivated({
      parentPageName: 'Assistant settings',
      successFlag: false,
      resultValue: error
    });
    throw error;
  }
});

export const checkMultilingualEnablementProgress =
  (
    assistantId: string,
    _multilingual: Multilingual
  ): AsyncAppThunk<Promise<void>> =>
  async dispatch => {
    const waitTimeInMs = 10000; // 10 seconds
    let multilingual = _multilingual;

    // Wait until the multilingual process is completed and dispatch updates in the meantime
    while (isProcessing(multilingual)) {
      try {
        await sleep(waitTimeInMs);
        const assistant = await dispatch(
          getAssistant({
            assistantId
          })
        ).unwrap();
        multilingual = assistant.multi_lingual;
      } catch (error) {
        dispatch(addErrorMessage(error));
      }
    }

    // Notify the user if multilingual is enabled
    if (multilingual.status === MultilingualStatusType.COMPLETED) {
      await dispatch(
        addMessage({
          message: 'Multilingual is active',
          status: ToastStatus.SUCCESS
        })
      );
      ranProcessEvents.multilingualActivated({
        parentPageName: 'Assistant settings',
        successFlag: true,
        resultValue: 'success'
      });
    }

    // Notify the user if multilingual failed to enable
    if (multilingual.status === MultilingualStatusType.FAILED) {
      await dispatch(
        addMessage({
          message: 'Multilingual failed to activate. Please try again',
          status: ToastStatus.ERROR
        })
      );
      ranProcessEvents.multilingualActivated({
        parentPageName: 'Assistant settings',
        successFlag: false,
        resultValue: 'failed'
      });
    }

    return;
  };

type MultilingualExportProps = {
  assistant_id: string;
  release_id: string;
};

export const exportMultilingual = createAsyncThunk<
  Multilingual,
  MultilingualExportProps,
  CreateAsyncThunk
>(
  'multilingual/export/create',
  async ({ assistant_id, release_id }, { dispatch }) => {
    try {
      const multilingual = await releasesAPI.createMultilingualExport(
        assistant_id,
        release_id
      );

      if (multilingual.status === MultilingualStatusType.COMPLETED) {
        const response = await dispatch(
          getMultilingualExport({
            assistant_id: assistant_id,
            release_id: release_id
          })
        ).unwrap();
        await handleGetMultilingualExportResponse(
          response,
          dispatch,
          assistant_id,
          release_id,
          DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
        );
      } else if (
        multilingual.status === MultilingualStatusType.FAILED ||
        multilingual.code
      ) {
        // Notify user multilingual file package failed to download if the status from createMultilingualExport is "Failed"
        // or backend api returned an error code eg. {code : 404, error : 'Resource not found'}
        await addMultilingualDownloadErrorMsg(
          dispatch,
          DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
        );
      } else {
        // Kick off a process to check on the multilingual download status
        await dispatch(
          checkMultilingualDownloadProgress(
            assistant_id,
            release_id,
            multilingual
          )
        );
      }

      return multilingual;
    } catch (error) {
      await addMultilingualDownloadErrorMsg(
        dispatch,
        DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
      );
    }
  }
);

export const checkMultilingualDownloadProgress =
  (
    assistantId: string,
    releaseId: string,
    _multilingual: Multilingual
  ): AsyncAppThunk<Promise<void>> =>
  async dispatch => {
    const waitTimeInMs = 10000; // 10 seconds
    let multilingual = _multilingual;

    // Notify user multilingual download is processing
    await addMultilingualLoadingMsg(dispatch, TabType.DOWNLOAD);

    // Wait until the multilingual process is complete and dispatch updates in the meantime
    while (isProcessing(multilingual)) {
      try {
        await sleep(waitTimeInMs);
        multilingual = await dispatch(
          getMultilingualExport({
            assistant_id: assistantId,
            release_id: releaseId
          })
        ).unwrap();
      } catch (error) {
        dispatch(addErrorMessage(error));
      }
    }

    await handleGetMultilingualExportResponse(
      multilingual,
      dispatch,
      assistantId,
      releaseId,
      DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
    );

    return;
  };

export const getMultilingualExport = createAsyncThunk<
  Multilingual,
  MultilingualExportProps,
  CreateAsyncThunk
>(
  'multilingual/export/get',
  async ({ assistant_id, release_id }, { dispatch }) => {
    try {
      const multilingualExportResponse =
        await releasesAPI.getMultilingualExport(assistant_id, release_id);
      return multilingualExportResponse;
    } catch (error) {
      await dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

async function handleGetMultilingualExportResponse(
  multilingual: Multilingual,
  dispatch: AppDispatch,
  assistantId: string,
  releaseId: string,
  exportType: DownloadUploadType
) {
  let errorMessage: string;
  let fileName: string;
  let downloadCategory: string;
  switch (exportType) {
    case DownloadUploadType.MULTILINGUAL_FILE_PACKAGE:
      errorMessage =
        'Multilingual file package failed to download. Please try again';
      fileName = `IBM-WA-multilingual-${assistantId}-V${releaseId}.zip`;
      downloadCategory = DownloadUploadType.MULTILINGUAL_FILE_PACKAGE;
      break;
    case DownloadUploadType.ASSISTANT_ONLY:
      errorMessage = 'Assistant file failed to download. Please try again';
      fileName = `IBM-WA-${assistantId}-V${releaseId}.zip`;
      downloadCategory = DownloadUploadType.ASSISTANT_ONLY;
      break;
    default:
      break;
  }

  // Notify user that file package failed to download if the status is "Failed"
  // or backend api returned an error code eg. {code : 404, error : 'Resource not found'}
  if (
    multilingual.status === MultilingualStatusType.FAILED ||
    multilingual.code
  ) {
    await dispatch(
      addMessage({
        message: errorMessage,
        status: ToastStatus.ERROR
      })
    );
    ranProcessEvents.assistantDownloaded({
      parentPageName: 'Assistant settings',
      category: downloadCategory,
      successFlag: false,
      resultValue: 'failed'
    });
  } else {
    // File package download succeed, return the zip file to user
    triggerSaveZip(multilingual, fileName);

    // Notify user multilingual download is complete
    await dispatch(
      addMessage({
        message: 'Your file download is complete.',
        status: ToastStatus.SUCCESS
      })
    );
    ranProcessEvents.assistantDownloaded({
      parentPageName: 'Assistant settings',
      category: downloadCategory,
      successFlag: true,
      resultValue: 'success'
    });
  }
}

export const exportAssistantRelease = createAsyncThunk<
  Multilingual,
  MultilingualExportProps,
  CreateAsyncThunk
>(
  'multilingual/assistant/export/create',
  async ({ assistant_id, release_id }, { dispatch }) => {
    try {
      const multilingual = await releasesAPI.createAssistantReleaseExport(
        assistant_id,
        release_id
      );

      if (multilingual.status === MultilingualStatusType.COMPLETED) {
        const response = await dispatch(
          getAssistantReleaseExport({
            assistant_id: assistant_id,
            release_id: release_id
          })
        ).unwrap();
        await handleGetMultilingualExportResponse(
          response,
          dispatch,
          assistant_id,
          release_id,
          DownloadUploadType.ASSISTANT_ONLY
        );
      } else if (
        multilingual.status === MultilingualStatusType.FAILED ||
        multilingual.code
      ) {
        // Notify user asssitant release data failed to download if the status from createAssistantReleaseExport is "Failed"
        // or backend api returned an error code eg. {code : 404, error : 'Resource not found'}
        await addMultilingualDownloadErrorMsg(
          dispatch,
          DownloadUploadType.ASSISTANT_ONLY
        );
      } else {
        // Kick off a polling process to check on the assistant release donwnload status
        await dispatch(
          checkAssistantReleaseDownloadProgress(
            assistant_id,
            release_id,
            DownloadUploadType.ASSISTANT_ONLY,
            multilingual
          )
        );
      }

      return multilingual;
    } catch (error) {
      await addMultilingualDownloadErrorMsg(
        dispatch,
        DownloadUploadType.ASSISTANT_ONLY
      );
    }
  }
);

export const checkAssistantReleaseDownloadProgress =
  (
    assistantId: string,
    releaseId: string,
    exportType: DownloadUploadType,
    _multilingual: Multilingual
  ): AsyncAppThunk<Promise<void>> =>
  async dispatch => {
    const waitTimeInMs = 10000; // 10 seconds
    let multilingual = _multilingual;

    // Notify user that assistant data download is processing
    await addMultilingualLoadingMsg(dispatch, TabType.DOWNLOAD);

    // Wait until the assistant release export process is complete and dispatch updates in the meantime
    while (isProcessing(multilingual)) {
      try {
        await sleep(waitTimeInMs);
        multilingual = await dispatch(
          getAssistantReleaseExport({
            assistant_id: assistantId,
            release_id: releaseId
          })
        ).unwrap();
      } catch (error) {
        dispatch(addErrorMessage(error));
      }
    }

    await handleGetMultilingualExportResponse(
      multilingual,
      dispatch,
      assistantId,
      releaseId,
      exportType
    );

    return;
  };

export const getAssistantReleaseExport = createAsyncThunk<
  Multilingual,
  MultilingualExportProps,
  CreateAsyncThunk
>(
  'multilingual/assistant/export/get',
  async ({ assistant_id, release_id }, { dispatch }) => {
    try {
      const assistantReleaseExportResponse =
        await releasesAPI.getAssistantReleaseExport(assistant_id, release_id);
      return assistantReleaseExportResponse;
    } catch (error) {
      await dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

type MultilingualImportProps = {
  assistant_id: string;
  actionSkill_id?: string;
  dialogSkill_id?: string;
  isDialogEnabled?: boolean;
  importedFile?: any;
};

/**
 * @param {string} params.assistant_id The assistant id
 * @param {string} params.actionSkill_id The action skill id
 * @param {string} params.dialogSkill_id The dialog skill id
 * @param {boolean} params.isDialogEnabled Indicate whether dialog is enabled or not in the assistant
 * @param {*} params.importedFile The multilingual zip file to import
 * @returns {AsyncThunk} The async action
 */
export const importMultilingual = createAsyncThunk<
  Multilingual,
  MultilingualImportProps,
  CreateAsyncThunk
>('multilingual/import/create', async (params, { dispatch }) => {
  const {
    assistant_id,
    actionSkill_id,
    dialogSkill_id,
    isDialogEnabled,
    importedFile
  } = params;
  try {
    let multilingual: Multilingual = await API.createMultilingualImport(
      assistant_id,
      importedFile
    );

    if (multilingual.errors) {
      await addMultilingualUploadErrorMsg(
        dispatch,
        DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
      );
    } else {
      // Importing is async so we need to monitor it
      multilingual = await dispatch(
        checkMultilingualImportProgress(
          assistant_id,
          actionSkill_id,
          dialogSkill_id,
          isDialogEnabled,
          multilingual
        )
      );
    }

    return multilingual;
  } catch (error) {
    await addMultilingualUploadErrorMsg(
      dispatch,
      DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
    );
  }
});

export const checkMultilingualImportProgress =
  (
    assistantId: string,
    actionSkillId: string,
    dialogSkillId: string,
    isDialogEnabled: boolean,
    multilingualReponse: Multilingual
  ): AsyncAppThunk<Promise<Multilingual>> =>
  async dispatch => {
    const waitTimeInMs = 15000; // 15 seconds
    let multilingual = multilingualReponse;

    // Notify user multilingual upload is processing
    await addMultilingualLoadingMsg(dispatch, TabType.UPLOAD);

    // Wait until the multilingual process is complete
    while (isProcessing(multilingual)) {
      try {
        await sleep(waitTimeInMs);
        multilingual = await dispatch(
          getMultilingualImport({
            assistant_id: assistantId
          })
        ).unwrap();
      } catch (error) {
        dispatch(addErrorMessage(error));
      }
    }

    // Notify the user if multilingual file upload is complete
    if (multilingual.status === MultilingualStatusType.COMPLETED) {
      await dispatch(
        addMessage({
          message: 'Your file upload is complete.',
          status: ToastStatus.SUCCESS
        })
      );
      ranProcessEvents.assistantUploaded({
        parentPageName: 'Assistant settings',
        category: DownloadUploadType.MULTILINGUAL_FILE_PACKAGE,
        successFlag: true,
        resultValue: 'success'
      });

      // After uploading the skill, we need to fetch Actions page and Dialog page
      await fetchActionsAndDialog(
        dispatch,
        actionSkillId,
        isDialogEnabled,
        dialogSkillId
      );
    }

    // Notify the user if multilingual file failed to upload
    if (multilingual.status === MultilingualStatusType.FAILED) {
      await addMultilingualUploadErrorMsg(
        dispatch,
        DownloadUploadType.MULTILINGUAL_FILE_PACKAGE
      );
    }

    return multilingual;
  };

export const getMultilingualImport = createAsyncThunk<
  Multilingual,
  MultilingualImportProps,
  CreateAsyncThunk
>('multilingual/import/get', async ({ assistant_id }, { dispatch }) => {
  try {
    const multilingualExportResponse = await API.getMultilingualImport(
      assistant_id
    );
    return multilingualExportResponse;
  } catch (error) {
    await dispatch(addErrorMessage(error));
    throw error;
  }
});

/**
 * @param {string} params.assistant_id The assistant id
 * @param {string} params.actionSkill_id The action skill id
 * @param {string} params.dialogSkill_id The dialog skill id
 * @param {boolean} params.isDialogEnabled Indicate whether dialog is enabled or not in the assistant
 * @param {*} params.importedFile The assistant release zip file to import
 * @returns {AsyncThunk} The async action
 */
export const importAssistantRelease = createAsyncThunk<
  Multilingual,
  MultilingualImportProps,
  CreateAsyncThunk
>('multilingual/assistant/import/create', async (params, { dispatch }) => {
  const {
    assistant_id,
    actionSkill_id,
    dialogSkill_id,
    isDialogEnabled,
    importedFile
  } = params;
  try {
    let multilingual: Multilingual = await API.createAssistantReleaseImport(
      assistant_id,
      importedFile
    );

    if (multilingual.errors) {
      await addMultilingualUploadErrorMsg(
        dispatch,
        DownloadUploadType.ASSISTANT_ONLY
      );
    } else {
      // Importing is async so we need to monitor it
      multilingual = await dispatch(
        checkAssistantReleaseImportProgress(
          assistant_id,
          actionSkill_id,
          dialogSkill_id,
          isDialogEnabled,
          multilingual
        )
      );
    }

    return multilingual;
  } catch (error) {
    await addMultilingualUploadErrorMsg(
      dispatch,
      DownloadUploadType.ASSISTANT_ONLY
    );
  }
});

export const checkAssistantReleaseImportProgress =
  (
    assistantId: string,
    actionSkillId: string,
    dialogSkillId: string,
    isDialogEnabled: boolean,
    multilingualResponse: Multilingual
  ): AsyncAppThunk<Promise<Multilingual>> =>
  async dispatch => {
    const waitTimeInMs = 15000; // 15 seconds
    let multilingual = multilingualResponse;

    // Notify user assistant upload is processing
    await addMultilingualLoadingMsg(dispatch, TabType.UPLOAD);

    // Wait until the process is complete
    while (isProcessing(multilingual)) {
      try {
        await sleep(waitTimeInMs);
        multilingual = await dispatch(
          getAssistantReleaseImport({
            assistant_id: assistantId
          })
        ).unwrap();
      } catch (error) {
        dispatch(addErrorMessage(error));
      }
    }

    // Notify the user if assistant file upload is complete
    if (multilingual.status === MultilingualStatusType.COMPLETED) {
      await dispatch(
        addMessage({
          message: 'Your file upload is complete.',
          status: ToastStatus.SUCCESS
        })
      );
      ranProcessEvents.assistantUploaded({
        parentPageName: 'Assistant settings',
        category: DownloadUploadType.ASSISTANT_ONLY,
        successFlag: true,
        resultValue: 'success'
      });

      // After uploading the skill, we need to fetch Actions page and Dialog page
      await fetchActionsAndDialog(
        dispatch,
        actionSkillId,
        isDialogEnabled,
        dialogSkillId
      );
    }

    // Notify the user if assistant file failed to upload
    if (multilingual.status === MultilingualStatusType.FAILED) {
      await addMultilingualUploadErrorMsg(
        dispatch,
        DownloadUploadType.ASSISTANT_ONLY
      );
    }

    return multilingual;
  };

export const getAssistantReleaseImport = createAsyncThunk<
  Multilingual,
  MultilingualImportProps,
  CreateAsyncThunk
>(
  'multilingual/assistant/import/get',
  async ({ assistant_id }, { dispatch }) => {
    try {
      const multilingualExportResponse = await API.getAssistantReleaseImport(
        assistant_id
      );
      return multilingualExportResponse;
    } catch (error) {
      await dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

async function fetchActionsAndDialog(
  dispatch: AppDispatch,
  actionSkillId: string,
  isDialogEnabled: boolean,
  dialogSkillId: string
) {
  // After uploading the skill, we need to list actions and entities to get the latest content on Actions page
  await dispatch(listActions(actionSkillId));
  await dispatch(listVariables(actionSkillId));
  await dispatch(fetchIntents(actionSkillId));
  await dispatch(fetchEntities(actionSkillId, {}, true, true));
  await dispatch(listDataTypes());
  await dispatch(listSystemDataTypes({}));
  await dispatch(getCounterExamples(actionSkillId));

  // Check dialog activation status
  // If the uploaded zip file contains dialog, backend will enable dialog automatically
  await dispatch(listEnvironments());

  // If Dialog is enabled in the assistant
  // After uploading, fetch dialog nodes/intents/entities to get the latest content on Dialog page
  if (isDialogEnabled) {
    await dispatch(fetchNodes(dialogSkillId));
    await dispatch(fetchIntents(dialogSkillId));
    await dispatch(fetchEntities(dialogSkillId, {}, {}, {}));
  }
}

async function addMultilingualUploadErrorMsg(
  dispatch: AppDispatch,
  uploadType: DownloadUploadType
) {
  await dispatch(
    addMessage({
      message: 'Refer to the assistant settings page for details.',
      status: ToastStatus.ERROR,
      title: 'There are errors with your file upload'
    })
  );
  ranProcessEvents.assistantUploaded({
    parentPageName: 'Assistant settings',
    category: uploadType,
    successFlag: false,
    resultValue: 'failed'
  });
}

async function addMultilingualLoadingMsg(
  dispatch: AppDispatch,
  selectionType: TabType
) {
  const title: string =
    selectionType === TabType.DOWNLOAD
      ? 'Your file download has been initiated.'
      : 'Your file upload has been initiated.';
  await dispatch(
    addMessage({
      message: 'You will receive another message when it is complete.',
      status: ToastStatus.INFO,
      title: title
    })
  );
}

async function addMultilingualDownloadErrorMsg(
  dispatch: AppDispatch,
  downloadType: DownloadUploadType
) {
  const message: string =
    downloadType === DownloadUploadType.ASSISTANT_ONLY
      ? 'Assistant file failed to download. Please try again'
      : 'Multilingual file package failed to download. Please try again';
  await dispatch(
    addMessage({
      message: message,
      status: ToastStatus.ERROR
    })
  );
  ranProcessEvents.assistantDownloaded({
    parentPageName: 'Assistant settings',
    category: downloadType,
    successFlag: false,
    resultValue: 'failed'
  });
}
